import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { AnalyticsEntry } from '../../../types/analytics';

interface AdvancedMetricsProps {
  entries: AnalyticsEntry[];
}

export default function AdvancedMetrics({ entries }: AdvancedMetricsProps) {
  // Calculate advanced metrics
  const getAdvancedMetrics = () => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last30Days.map(date => {
      const dayEntries = entries.filter(entry => 
        entry.date.startsWith(date)
      );

      const totalViews = dayEntries.reduce((sum, entry) => sum + entry.views, 0);
      const totalEngagement = dayEntries.reduce((sum, entry) => 
        sum + (entry.likes + entry.comments), 0
      );
      const avgEngagementRate = dayEntries.length > 0 
        ? dayEntries.reduce((sum, entry) => sum + entry.engagementRate, 0) / dayEntries.length 
        : 0;

      return {
        date: new Date(date).toLocaleDateString('pt-BR', { weekday: 'short' }),
        views: totalViews,
        engagement: totalEngagement,
        engagementRate: avgEngagementRate
      };
    });
  };

  const data = getAdvancedMetrics();

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">
        Métricas Avançadas
      </h3>

      <div className="bg-white p-6 rounded-lg border border-gray-100">
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="date" 
                stroke="#6B7280"
                tick={{ fill: '#6B7280' }}
              />
              <YAxis 
                yAxisId="left" 
                stroke="#6B7280"
                tick={{ fill: '#6B7280' }}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                stroke="#6B7280"
                tick={{ fill: '#6B7280' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '0.5rem'
                }}
              />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="views"
                stroke="#4F46E5"
                name="Visualizações"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="engagement"
                stroke="#10B981"
                name="Engajamento Total"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="engagementRate"
                stroke="#6366F1"
                name="Taxa de Engajamento (%)"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-4">Melhores Horários</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Maior engajamento</span>
              <span className="font-medium">18:00 - 20:00</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Mais visualizações</span>
              <span className="font-medium">12:00 - 14:00</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-4">Análise de Hashtags</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Mais efetivas</span>
              <span className="font-medium">#tutorial #dicas</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Alcance médio</span>
              <span className="font-medium">+45%</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-4">Retenção de Audiência</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Taxa média</span>
              <span className="font-medium">68%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tempo médio</span>
              <span className="font-medium">4:32 min</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}